// Record how this person reads dates — the locale their dates are written in
// here and their device's time zone — so messages the SERVER writes for them
// (a Google Chat message, a push alert, an email) use the same notation and
// zone as the app. Stored in their own users/{uid}/settings/notifications as
// `locale` and `timeZone`; apps/functions/src/lib/recipientFormat.ts reads it.
//
// Written only when it changes (a new browser language, a trip abroad): the
// last value written is remembered on the device, so an ordinary launch
// costs nothing. It waits for Firebase Auth's own verdict first, like
// lib/usage.ts — the shell can render from the persisted session before Auth
// confirms it, and nothing may write on the strength of that (CLAUDE.md § 5.6).
// Pure preference: never an input to an authorization decision.

import { setDoc } from 'firebase/firestore';

import { auth } from './firebase';
import { displayLocale } from './dateFormat';
import { deviceTimeZone, notificationSettingsRef } from './notificationSettings';

const STORAGE_PREFIX = 'onlogic.displayFormat.';

/** Read a remembered value; storage can be unavailable (private mode). */
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Without storage this simply writes again on the next launch.
  }
}

/** Record this device's date notation and time zone for the signed-in user, if changed. */
export async function syncDisplayFormat(): Promise<void> {
  const locale = displayLocale();
  const timeZone = deviceTimeZone();
  if (!locale && !timeZone) return;

  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return;

  const key = `${STORAGE_PREFIX}${user.uid}`;
  const value = JSON.stringify({ locale: locale ?? null, timeZone: timeZone ?? null });
  if (readStored(key) === value) return;

  try {
    await setDoc(
      notificationSettingsRef(user.uid),
      { ...(locale ? { locale } : {}), ...(timeZone ? { timeZone } : {}) },
      { merge: true },
    );
    writeStored(key, value);
  } catch (err) {
    // Not worth disturbing anyone over; the next launch tries again.
    // eslint-disable-next-line no-console
    console.warn('Could not record the date format for server messages:', err);
  }
}
